import React from "react"
import Ample from "../components/ample/ample"
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LLMContent from "../components/LLMContent";
import EXAMPLES from "../data/chat_examples.json";
import { TOOLS, getToolPageTitle } from "../optiontools";
import { logEvent } from "../analytics";

const CHAT_REQUEST_TIMEOUT_MS = 12000;
const CHAT_REQUEST_TIMEOUT_SECONDS = CHAT_REQUEST_TIMEOUT_MS / 1000;

// Decode JWT payload and extract the exp (expiry in seconds since epoch)
const getJwtExpSeconds = (token) => {
  try {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null; // not a JWT
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    if (!payload || typeof payload.exp !== 'number') return null;
    return payload.exp;
  } catch (e) {
    return null;
  }
}

// Consider a token expired if current time is past exp minus a small skew
const isTokenExpired = (token, skewSeconds = 30) => {
  const exp = getJwtExpSeconds(token);
  if (typeof exp !== 'number') {
    // If we cannot determine expiry, treat as expired to be safe
    return true;
  }
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= (exp - skewSeconds);
}

const ChatPage = (props) => {
  const [query, setQuery] = React.useState("");
  const [submittedQuery, setSubmittedQuery] = React.useState("");
  const [messageCount, setMessageCount] = React.useState(0);
  const [isAwaitingResponse, setIsAwaitingResponse] = React.useState(false);
  const [responseText, setResponseText] = React.useState("");
  const [errorText, setErrorText] = React.useState("");
  const [placeholderIndex, setPlaceholderIndex] = React.useState(0);
  const rotate = React.useRef(null);

  const localStorageKey = "optionchat_session_token";

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedToken = window.localStorage.getItem(localStorageKey);
    if (!(storedToken && storedToken.trim().length > 0 && !isTokenExpired(storedToken)) && storedToken) {
      // Clear expired tokens
      window.localStorage.removeItem(localStorageKey);
    }
  }, []);

  React.useEffect(() => {
    const shouldRotate = submittedQuery.trim().length === 0;
    if (shouldRotate) {
      rotate.current = window.setInterval(() => {
        setPlaceholderIndex((i) => (i + 1) % EXAMPLES.length);
      }, 3500);
    }
    return () => {
      if (rotate.current) window.clearInterval(rotate.current);
      rotate.current = null;
    };
  }, [submittedQuery]);

  const placeholder = React.useMemo(() => `e.g., ${EXAMPLES[placeholderIndex]}`, [placeholderIndex]);

  /*
  Ensure a session token is available.
  If one is already in local storage and is not expired, return it.
  otherwise, create a new one and return it.
  */
  const ensureSession = async () => {
    if (typeof window === 'undefined') return;

    // first check if a token is already in local storage 
    const storedToken = window.localStorage.getItem(localStorageKey);
    if (storedToken && storedToken.trim().length > 0 && !isTokenExpired(storedToken)) {
      return storedToken;
    } else if (storedToken) {
      // Remove expired/invalid token before creating a new one
      window.localStorage.removeItem(localStorageKey);
    }
    // If no token is available, create a new one
    try {
      const url = `${process.env.GATSBY_OPTIONS_API_ENDPOINT}/chat/session`;
      console.log("fetching option chat session from ", url);
      // Use a simple POST without custom headers to avoid browser preflight
      const res = await fetch(url, { method: 'POST' });
      if (!res.ok) {
        logEvent("chat_session_create_Failed", { status: res.status, statusText: res.statusText });
        throw new Error(`Failed to create session: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      const token = (data && (data.token || data.session || data.id || data.sessionId)) ? (data.token || data.session || data.id || data.sessionId) : "";
      if (token && token.length > 0) {
        window.localStorage.setItem(localStorageKey, token);
        logEvent("chat_session_create_Success");
      } else {
        logEvent("chat_session_create_Failed", { error: "No token in response" });
      }
      return token;
    } catch (err) {
      console.error('Error creating chat session', err);
      logEvent("chat_session_create_Failed", { error: err.message });
      return "";
    } 
  }

  const submitQuery = async (retryQuery = null) => {
    const trimmed = retryQuery || query.trim();
    if (trimmed.length === 0) return;

    if (!retryQuery) {
      const nextCount = messageCount + 1;
      setMessageCount(nextCount);
      if (nextCount === 1) {
        logEvent("chat_message_sent_first");
      } else if (nextCount === 3) {
        logEvent("chat_message_sent_third");
      }
    }

    setErrorText("");
    setResponseText("");
    setSubmittedQuery(trimmed);
    if (!retryQuery) setQuery("");
    setIsAwaitingResponse(true);
    
    const token = await ensureSession();
    if (!token) {
      setErrorText("Failed to create session. Please try again.");
      setIsAwaitingResponse(false);
      return;
    }
    
    // Abort slightly above typical 6–8s ChatMessages round-trips without waiting 20s.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, CHAT_REQUEST_TIMEOUT_MS);
    
    try {
      const chatUrl = `${process.env.GATSBY_OPTIONS_API_ENDPOINT}/chat?query=${encodeURIComponent(trimmed)}`;
      
      const res = await fetch(chatUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Request failed: ${res.status} ${res.statusText} ${txt}`);
      }
      let data;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        try { data = JSON.parse(text); } catch { data = { response: text }; }
      }
      const reply = data && (data.response || data.message || data.output);
      setResponseText(reply || "No response.");
    } catch (e) {
      clearTimeout(timeoutId);
      
      console.error('Chat request error', e);
      
      if (e.name === 'AbortError') {
        logEvent("chat_request_Aborted", { query: trimmed });
        setErrorText(`Request timed out after ${CHAT_REQUEST_TIMEOUT_SECONDS} seconds. This may be due to a cold start or complex query. Please retry - it should be faster the second time.`);
      } else {
        logEvent("chat_request_Failed", { error: e.message, query: trimmed });
        setErrorText('Error getting response. Please try again.');
      }
    } finally {
      setIsAwaitingResponse(false);
    }
  }
  
  return (
    <Ample currPageTitle={getToolPageTitle(TOOLS.CHAT)} location={props.location.href}>
      <h1 style={{position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0}}>
        {getToolPageTitle(TOOLS.CHAT)}
      </h1>
      <div className="row">
          <div className="col-md-12">
              <div className="white-box">
                  <div className="box-title" style={{textTransform: 'none'}}>Options Chat</div>
                  <Card style={{marginBottom:"16px"}}>
                    <CardContent>
                      {submittedQuery ? (
                        <>
                          <div style={{display:'flex', justifyContent:'flex-end', marginBottom:'8px'}}>
                            <Box sx={{ bgcolor:'primary.main', color:'primary.contrastText', px:2, py:1, borderRadius:2, maxWidth:'75%'}}>
                              {submittedQuery}
                            </Box>
                          </div>
                          {isAwaitingResponse ? (
                            <div style={{minHeight:'56px', display:'flex', alignItems:'center'}}>
                              <CircularProgress size={16} style={{marginRight:'8px'}} />
                              <Typography variant="body2" color="text.secondary">Waiting for a response…</Typography>
                            </div>
                          ) : errorText ? (
                            <>
                              <Typography variant="body2" color="error" style={{marginBottom:'12px'}}>{errorText}</Typography>
                              <div style={{display:'flex', justifyContent:'flex-start', gap:'8px'}}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    logEvent("chat_retry_click");
                                    submitQuery(submittedQuery);
                                  }}
                                  className="btn btn-rounded btn-default btn-outline btn-sm"
                                  style={{fontSize:'12px', padding:'4px 12px'}}
                                >
                                  <i className="fas fa-redo m-r-5"></i>Retry
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    logEvent("chat_new_chat_click");
                                    setSubmittedQuery("");
                                    setResponseText("");
                                    setErrorText("");
                                    setQuery("");
                                  }}
                                  className="btn btn-rounded btn-default btn-outline btn-sm"
                                  style={{fontSize:'12px', padding:'4px 12px'}}
                                >
                                  <i className="fas fa-plus m-r-5"></i>New Chat
                                </button>
                              </div>
                            </>
                          ) : responseText ? (
                            <>
                              <div style={{display:'flex', justifyContent:'flex-start', marginTop:'8px'}}>
                                <Box sx={{ bgcolor:'grey.100', color:'text.primary', px:2, py:1, borderRadius:2, maxWidth:'75%'}}>
                                  <LLMContent text={responseText} />
                                </Box>
                              </div>
                              <div style={{display:'flex', justifyContent:'flex-start', marginTop:'12px'}}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    logEvent("chat_new_chat_click");
                                    setSubmittedQuery("");
                                    setResponseText("");
                                    setErrorText("");
                                    setQuery("");
                                  }}
                                  className="btn btn-rounded btn-default btn-outline btn-sm"
                                  style={{fontSize:'12px', padding:'4px 12px'}}
                                >
                                  <i className="fas fa-plus m-r-5"></i>New Chat
                                </button>
                              </div>
                            </>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary" style={{ marginBottom: '8px' }}>
                            Ask about max pain levels, unusual activity, payoff diagrams, or strategy analysis.
                          </Typography>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body2" color="text.secondary" style={{ minWidth: '480px' }}>
                              Example: {EXAMPLES[placeholderIndex]}
                            </Typography>
                            <button
                              type="button"
                              onClick={() => {
                                logEvent("chat_example_click", { example: EXAMPLES[placeholderIndex] });
                                setQuery(EXAMPLES[placeholderIndex]);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                color: '#666',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                flexShrink: 0
                              }}
                              title="Ask this question"
                            >
                              <i className="fas fa-arrow-right" style={{ fontSize: '10px' }}></i>
                              Ask this
                            </button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  <TextField
                    id="chatQuery"
                    label="Ask about options or the market"
                    placeholder={placeholder}
                    variant="outlined"
                    fullWidth
                    value={query}
                    onChange={(e)=>setQuery(e.target.value)}
                    onKeyDown={(e)=>{ if (e.key==='Enter') { e.preventDefault(); submitQuery(); } }}
                    helperText={query.trim().length === 0 ? "Tip: Press Enter to send." : "Press Enter to send."}
                    aria-label="Chat query"
                    style={{marginTop:"8px"}}
                  />
                  <div className="row" style={{marginTop:"8px"}}>
                    <div className="col-md-12" style={{display:'flex', justifyContent:'flex-end'}}>
                      <button id="send" onClick={() => submitQuery()} type="button" className="btn btn-rounded btn-default btn-outline" disabled={query.trim().length === 0}>
                        <i className="fas fa-paper-plane m-r-5"></i>Send
                      </button>
                    </div>
                  </div>
              </div>
          </div>
          </div>
    </Ample>);
};
export default ChatPage;